import { Colors } from "@/enums/Colors";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { SearchOutlined } from "@mui/icons-material";
import { Box, BoxProps, InputAdornment, SxProps, TextField, Typography, styled } from "@mui/material";
import { RefObject, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import CommonButton, { CommonButtonProps } from "./CommonButton";
import { SearchIcon } from "./icons/SearchIcon";

const StyledTextField = styled(TextField)(({}) => ({
  "& .MuiInputBase-root": {
    height: "2.5rem",
    borderRadius: ".7rem",
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    },
  },
}));

type SearchFieldProps = {
  handleSearch: (searchRef: RefObject<HTMLInputElement>) => void;
  displaySearchButton: boolean;
  defaultValue?: string | null;
  containerProps?: BoxProps;
  buttonProps?: CommonButtonProps;
  fullWidth?: boolean;
  triggerSearchOnInputChange: boolean;
  sx?: SxProps;
  iconVariant?: boolean;
  displayIcon?: boolean;
  placeholder?: string;
  debouncer?: Debouncer;
  search?: string;
  setSearch?: (search: string) => void;
  route?: string;
  setRoute?: (route: string) => void;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
};

const debouncer = new Debouncer(250);

function SearchField(props: SearchFieldProps) {
  const { t } = useTranslation("common");
  const { displayIcon = true } = props;

  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.value = props.search || "";
    }
  }, [props.search]);

  useEffect(() => {
    const actualRoute = window.location.pathname;
    if (props.route && props.route !== actualRoute) {
      props.setSearch?.("");
    }
  }, [props.route]);

  function triggerSearch() {
    if (props.debouncer) {
      props.debouncer.executeLastCall(() => props.handleSearch(searchRef));
    } else {
      debouncer.executeLastCall(() => props.handleSearch(searchRef));
    }
  }

  return (
    <Box {...props.containerProps}>
      <StyledTextField
        onClick={props.onClick}
        variant="outlined"
        inputRef={searchRef}
        onChange={props.triggerSearchOnInputChange ? triggerSearch : () => {}}
        onKeyDown={(e) => e.key === "Enter" && triggerSearch()}
        fullWidth={props.fullWidth}
        defaultValue={props.defaultValue}
        placeholder={props.placeholder || t("search")}
        InputProps={{
          startAdornment: displayIcon && (
            <InputAdornment position="start">
              {props.iconVariant ? (
                <SearchIcon sxClass={{ fontSize: "1rem" }} />
              ) : (
                <SearchOutlined
                  sx={{
                    transform: "scale(1.6)",
                    "&.MuiSvgIcon-root": {
                      color: Colors.LIGHT_GRAY_BFC2C6,
                    },
                  }}
                />
              )}
            </InputAdornment>
          ),
        }}
        sx={props.sx}
      />
      {props.displaySearchButton && (
        <CommonButton {...props.buttonProps} onClick={triggerSearch}>
          <Typography>{t("search")}</Typography>
        </CommonButton>
      )}
    </Box>
  );
}

export default SearchField;
